import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { tasksPageText } from '../../constants.ts';
import { tasksPageStyles } from '../../styles.ts';
import { MILLISECONDS_IN_SECOND } from './constants.ts';
import type { TTaskSlaIndicatorProps } from './types.ts';
import {
  formatTaskSlaCountdown,
  formatTaskSlaDeadline,
  getTaskSlaPulseLevel,
  getTaskSlaRemainingSeconds,
} from './utils.ts';

export const TaskSlaIndicator: React.FC<TTaskSlaIndicatorProps> = ({
  deadline,
  onDeadlineReached,
  overdue = false,
}) => {
  const [now, setNow] = useState(() => Date.now());
  const deadlineReachedNotifiedRef = useRef(false);
  const formattedDeadline = useMemo(
    () => (deadline ? formatTaskSlaDeadline(deadline) : ''),
    [deadline]
  );
  const remainingSeconds = deadline ? getTaskSlaRemainingSeconds(deadline, now) : 0;
  const pulseLevel = getTaskSlaPulseLevel(remainingSeconds, overdue);

  useEffect(() => {
    deadlineReachedNotifiedRef.current = false;
    setNow(Date.now());
  }, [deadline]);

  useEffect(() => {
    if (!deadline || overdue) {
      return undefined;
    }

    const intervalId = window.setInterval(() => setNow(Date.now()), MILLISECONDS_IN_SECOND);

    return () => window.clearInterval(intervalId);
  }, [deadline, overdue]);

  useEffect(() => {
    if (!deadline || overdue || remainingSeconds > 0 || deadlineReachedNotifiedRef.current) {
      return;
    }

    deadlineReachedNotifiedRef.current = true;
    onDeadlineReached?.();
  }, [deadline, onDeadlineReached, overdue, remainingSeconds]);

  if (!deadline || !formattedDeadline) {
    return null;
  }

  return (
    <Box sx={tasksPageStyles.slaWrap(overdue)}>
      <Box sx={tasksPageStyles.slaHeader}>
        <Box
          component="span"
          aria-hidden="true"
          data-pulse-level={pulseLevel}
          data-testid="task-sla-pulse-dot"
          sx={tasksPageStyles.slaPulseDot(pulseLevel)}
        />
        <Box sx={tasksPageStyles.slaTextWrap}>
          <Typography variant="caption" sx={tasksPageStyles.slaDate}>
            {formattedDeadline}
          </Typography>
          <Typography
            variant="caption"
            sx={overdue ? tasksPageStyles.slaOverdueText : tasksPageStyles.slaCountdown(false)}
          >
            {overdue
              ? tasksPageText.guaranteesSlaOverdueLabel
              : formatTaskSlaCountdown(remainingSeconds)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
